import { Box, IconButton, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { ArrowRight, InfoCircle, StatusAlertFilledSmall } from '@wix/wix-ui-icons-common';
import classnames from 'classnames';
import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { AttachingMethod, TLocalesKeys as K } from '../../../../types';
import { selectAttachedFiles, selectAttachedIsLoading } from '../../../common/selectors/attaches';
import { selectIsLiveChat } from '../../../common/selectors/chat';
import { selectFieldState, selectHasLimitError } from '../../../common/selectors/field';
import { selectIsCopilotInitialized } from '../../../common/selectors/meta';
import { useAppDispatch } from '../../../common/store';
import { loadAttaches } from '../../../common/thunks/attaches.thunk';
import { sendMessageReady } from '../../../messageChannel/postNewMessages';
import { renderModel } from '../../../util/renderModel';
import { useIsAttachmentAvailable, useIsSmallScreen, useUserActionSendMessage } from '../../hooks';
import { AttachFile } from './AttachFile';
import { AttachmentsPreview } from './AttachmentsPreview';
import { AttachmentsStatus } from './AttachmentsStatus';
import { DirectSupportButton } from './DirectSupportButton';
import { Input } from './Input';
import { selectIsHidden } from './selectors';
import s from './styles.scss';

const isCopilot = renderModel.isCopilot();

export const Field: FC = React.memo(() => {
  const { value, disabled, multiline, maxLength } = useSelector(selectFieldState);
  const isImageLoading = useSelector(selectAttachedIsLoading);
  const isLiveChat = useSelector(selectIsLiveChat);
  const attachments = useSelector(selectAttachedFiles);
  const hasLimitError = useSelector(selectHasLimitError);
  const isCopilotInitialized = useSelector(selectIsCopilotInitialized);
  const isHidden = useSelector(selectIsHidden);
  const isMobile = renderModel.isMobile();
  const isSmallScreen = useIsSmallScreen();

  const dispatch = useAppDispatch();
  const emit = useUserActionSendMessage();
  const { t } = useTranslation();
  const isAttachmentAvailable = useIsAttachmentAvailable();
  const loadAttachesPromiseRef = useRef<ReturnType<ReturnType<typeof loadAttaches>> | null>(null);

  const isDisabled = (disabled || hasLimitError) && !isLiveChat;
  const hasAttachment = attachments.length > 0;
  const isSendDisabled = isDisabled || isImageLoading || !(value || hasAttachment);
  const isMultiline = !isMobile && multiline;

  const send = useCallback(
    (action_type: string) => {
      if (!isSendDisabled) {
        emit({
          value: value ? value.trim() : t<K>('attach_message'),
          action_type,
          attachments,
          isLiveChat,
        });
      }
    },
    [attachments, emit, isLiveChat, isSendDisabled, value, t],
  );

  const onSubmit = useCallback(() => {
    send('click');
  }, [send]);

  const onFilesUpload = useCallback(
    (files: File[], attachingMethod: AttachingMethod) => {
      loadAttachesPromiseRef.current = dispatch(loadAttaches({ files, attachingMethod }));
    },
    [dispatch],
  );

  const onFilesUploadCancel = useCallback(() => {
    loadAttachesPromiseRef.current?.abort();
  }, []);

  const classNames = useMemo(
    () =>
      classnames(s.field, {
        [s.hidden]: isHidden,
        [s.copilot]: isCopilot,
        [s.attachmentAvailable]: isAttachmentAvailable,
      }),
    [isHidden, isCopilot],
  );

  useEffect(() => {
    if (isCopilot && isCopilotInitialized) {
      sendMessageReady();
    }
  }, []);

  return (
    <Box className={classNames} position="relative" direction="vertical" dataHook="input-field">
      <DirectSupportButton />
      <Box
        dataHook="user-input"
        direction="vertical"
        gap="SP1"
        marginBottom={isSmallScreen ? (isCopilot ? 'SP2' : 'SP3') : 'SP5'}
      >
        <Box direction="vertical" gap="SP1" paddingTop="SP2" position="relative">
          <Box gap="1" verticalAlign="bottom" position="relative">
            <Box direction="vertical" flexGrow={1}>
              <Input rows={isCopilot ? 3 : undefined} isMultiline={isMultiline} onSend={send} />
            </Box>
            <Box
              direction="horizontal"
              gap="3px"
              className={classnames({ [s.copilotFieldActions]: isCopilot })}
              position="absolute"
              bottom="6px"
              right="9px"
            >
              {isAttachmentAvailable && <AttachFile onFilesUpload={onFilesUpload} />}
              <IconButton
                size="small"
                skin={isCopilot ? 'ai' : undefined}
                disabled={isSendDisabled}
                dataHook="send"
                onClick={onSubmit}
              >
                <ArrowRight />
              </IconButton>
            </Box>
          </Box>
          {isCopilot && (
            <Box className={s.inputAreaDisclaimer} gap="3px">
              <InfoCircle />
              <Text size="tiny" weight="thin">
                {t('chatbot_input_message_disclaimer')}
              </Text>
            </Box>
          )}
          {hasLimitError ? (
            <Text size="small">
              <Box direction="horizontal" gap="0.5" color="R10">
                <StatusAlertFilledSmall />
                <span>{t<K>('characters_count_error_message', { max: maxLength })}</span>
              </Box>
            </Text>
          ) : null}
        </Box>
        <AttachmentsStatus onFilesUploadCancel={onFilesUploadCancel} />
        {isAttachmentAvailable && attachments.length > 0 && (
          <Box gap="SP1" verticalAlign="middle">
            <AttachmentsPreview attachments={attachments} />
          </Box>
        )}
      </Box>
    </Box>
  );
});
